import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { startVisitSequence } from 'src/game/visit/visit.sequence';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import {
  HistoryDeathRecord,
  HistoryDuoRecord,
} from 'src/replicant/history/history.ruleset';
import { PetEvolutionStage, PetSpeciesID } from 'src/replicant/pet';
import { assertNever } from 'src/replicant/utils';
import { Immutable } from '@play-co/replicant';

type SpeciesCircle = {
  button: ButtonScaleView;
  circle: ImageScaleView;
  species: ImageScaleView;
  petName: LangBitmapFontTextView;
};

export type CurrentGenerationRecord = {
  type: 'current';
  species: PetSpeciesID;
  name: string;
  age: number;
  evolutions: readonly {
    stage: PetEvolutionStage;
    species: PetSpeciesID;
  }[];
};

export type FamilyTreeRecord = Immutable<
  HistoryDeathRecord | HistoryDuoRecord | CurrentGenerationRecord
>;

export class FamilyTreeTile extends View {
  private generationText: LangBitmapFontTextView;
  private petCircle: SpeciesCircle;
  private duoLine: ImageScaleView;
  private duoCircle: SpeciesCircle;
  private duoVerticalLine: ImageScaleView;
  private statusIcon: ImageScaleView;
  private deathVerticalLine: View;

  constructor(opts: ViewOpts) {
    super({ height: 245, width: 468, ...opts });

    const generationRibbon = new ImageScaleView({
      superview: this,
      image: 'assets/ui/family/frames/generation_ribbon.png',
      width: 160,
      height: 56,
      x: 10,
      y: 50,
    });

    this.generationText = new LangBitmapFontTextView({
      superview: generationRibbon,
      x: generationRibbon.style.width / 2,
      y: generationRibbon.style.height / 2,
      width: generationRibbon.style.width - 50,
      height: 30,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      color: '#9A7171',
      font: bitmapFonts('Body'),
      canHandleEvents: false,
      centerOnOrigin: true,
      localeText: () => `1st generation`,
    });

    this.petCircle = this.createPlayerSpeciesCircle(173, 20);

    this.statusIcon = new ImageScaleView({
      superview: this,
      x: 303,
      y: 24,
    });

    this.duoVerticalLine = new ImageScaleView({
      superview: this,
      image: 'assets/ui/family/frames/line.png',
      width: 20,
      height: 73,
      x: 224,
      y: 170,
    });

    this.deathVerticalLine = new View({
      superview: this,
      width: 20,
      height: 73,
      x: 224,
      y: 170,
    });

    for (let i = 0; i < 4; i++) {
      new ImageScaleView({
        superview: this.deathVerticalLine,
        image: `assets/ui/family/frames/line_deceased.png`,
        width: 21,
        height: 18,
        y: i * 23,
      });
    }

    // Only if married.

    this.duoLine = new ImageScaleView({
      superview: this,
      image: 'assets/ui/family/frames/line.png',
      width: 20,
      height: 73,
      r: Math.PI / 2,
      x: 352,
      y: 82,
      scale: 0.7,
    });

    this.duoCircle = this.createPlayerSpeciesCircle(355, 37, 0.85);
  }

  public setData(generation: number, data: FamilyTreeRecord) {
    this.generationText.localeText = () =>
      i18n('family.tree.generation', { generation });

    const { type, name, species, evolutions, age } = data;

    this.petCircle.petName.localeText = () => `${name} (${age})`;
    this.petCircle.species.setImage(`assets/ui/species/linepet_${species}.png`);
    this.petCircle.button.onClick = async () => {
      sounds.sfx.play('ok');
      await openPopupPromise('popupGrowthRecord', { name, evolutions });
    };

    // Change record based on type.
    switch (type) {
      case 'death':
        this.duoLine.hide();
        this.duoVerticalLine.hide();
        this.duoCircle.circle.hide();
        this.deathVerticalLine.show();
        this.petCircle.circle.setImage(
          `assets/ui/family/frames/character_circle_deceased.png`,
        );
        this.statusIcon.updateOpts({
          image: 'assets/ui/family/icons/deceased.png',
          width: 74,
          height: 68,
          scale: 1,
          visible: true,
        });
        break;
      case 'duo':
        this.duoLine.show();
        this.duoVerticalLine.show();
        this.duoCircle.circle.show();
        this.deathVerticalLine.hide();
        this.petCircle.circle.setImage(
          `assets/ui/family/frames/character_circle.png`,
        );
        this.statusIcon.updateOpts({
          image: 'assets/ui/family/icons/married.png',
          width: 58,
          height: 56,
          scale: 0.9,
          visible: true,
        });
        this.duoCircle.petName.localeText = () =>
          `${data.friend.name} (${data.friend.age})`;
        this.duoCircle.species.setImage(
          `assets/ui/species/linepet_${data.friend.species}.png`,
        );
        this.duoCircle.button.onClick = async () => {
          sounds.sfx.play('ok');
          await startVisitSequence({ friendId: data.friendID }, undefined);
        };
        break;
      case 'current':
        this.duoLine.hide();
        this.duoVerticalLine.hide();
        this.deathVerticalLine.hide();
        this.duoCircle.circle.hide();
        this.petCircle.circle.setImage(
          `assets/ui/family/frames/character_circle.png`,
        );
        this.statusIcon.hide();
        break;

      default:
        assertNever(type);
    }
  }

  private createPlayerSpeciesCircle(
    x: number,
    y: number,
    scale = 1,
  ): SpeciesCircle {
    const button = new ButtonScaleView({
      superview: this,
      x,
      y,
      width: 128,
      height: 128,
      scale,
      pressedOffsetY: 2,
      tracking: {
        feature: Feature.FAMILY._,
        $subFeature: Feature.FAMILY.TREE_POPUP.RECORD,
      },
    });

    const circle = new ImageScaleView({
      superview: button,
      image: 'assets/ui/family/frames/character_circle.png',
      width: 128,
      height: 128,
    });

    const species = new ImageScaleView({
      superview: circle,
      width: 90,
      height: 90,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: `assets/ui/species/linepet_ball.png`,
      x: circle.style.width * 0.5,
      y: circle.style.height * 0.5,
    });

    const petNameFrame = new ImageScaleView({
      superview: circle,
      image: `assets/ui/family/frames/pet_name.png`,
      centerOnOrigin: true,
      width: circle.style.width,
      height: 30,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 10, right: 10 },
        vertical: { top: 10, bottom: 10 },
      },
      x: circle.style.width / 2,
      y: circle.style.height - 5,
    });

    const petName = new LangBitmapFontTextView({
      superview: petNameFrame,
      x: petNameFrame.style.width / 2,
      y: petNameFrame.style.height / 2,
      width: petNameFrame.style.width - 15,
      height: 20,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      color: '#E27452',
      font: bitmapFonts('Body'),
      canHandleEvents: false,
      centerOnOrigin: true,
      localeText: () => `Pet Name`,
    });

    return { button, circle, petName, species };
  }
}
